import { createRouter, createWebHistory } from 'vue-router';
import AppLayout from '@/layout/AppLayout.vue';
import { useAuthStore } from '@/stores/auth';
import { useTabStore } from '@/stores/tab';
import { useLayoutStore } from '@/stores/layout';
import { nextTick } from 'vue';
import { checkIsMobile } from '@/utils/deviceDetection';
import MobileHome from '@/views/MobileHome.vue';
import IssueReportList from '@/views/issue/IssueReportList.vue'
import IssueReportDialog from '@/components/IssueReportDialog.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/:companyCode',
      name: 'company-entry',
      component: () => import('@/views/pages/CompanyEntry.vue'),
      beforeEnter: (to, from, next) => {
        // 로그인 페이지로의 리다이렉션은 컴포넌트에서 처리
        next();
      }
    },
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          beforeEnter: (to, from, next) => {
            const authStore = useAuthStore();
            // 벤더 사용자인 경우 벤더 대시보드로 리다이렉트
            if (authStore.isVendorUser) {
              next('/vendor/dashboard');
            } else if (checkIsMobile()) {
              // 일반 사용자이고 모바일인 경우 모바일 홈으로 리다이렉트
              next('/MobileHome');
            } else {
              // 일반 사용자이고 데스크톱인 경우 대시보드로 이동
              next();
            }
          }
        },
        {
          path: '/MobileHome',
          name: 'mobile-home-direct',
          component: MobileHome,
          beforeEnter: (to, from, next) => {
            const authStore = useAuthStore();
            // 벤더 사용자인 경우 벤더 대시보드로 리다이렉트
            if (authStore.isVendorUser) {
              next('/vendor/dashboard');
            } else {
              next();
            }
          }
        },
        //Vendor Dashboard
        {
          path: '/vendor/dashboard',
          name: 'vdashboard',
          component: () => import('@/views/VendorDashboard.vue'),
          meta: { 
            requiresAuth: true,
            isVendorRoute: true // 벤더 전용 라우트 표시
          }
        },
        {
          path: '/debug',
          name: 'debugDashboard',
          component: () => import('@/views/pages/debugDashboard.vue'),
        },
        {
          path: '/user',
          name: 'userSet',
          component: () => import('@/views/pages/auth/userSet.vue'),
        },
        //Asset Management
        {
          path: '/asset/AssetList',
          name: 'AssetList',
          component: () => import('@/views/asset/AssetList.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 자산관리 자산분류관리
        {
          path: '/asset/AssetCatMng',
          name: 'AssetCatMng',
          component: () => import('@/views/asset/AssetCatMng.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 자산관리 자산조회
        {
          path: '/asset/AssetComList',
          name: 'AssetComList',
          component: () => import('@/views/asset/AssetComList.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 자산관리 자산등록
        {
          path: '/asset/AssetComReg',
          name: 'AssetComReg',
          component: () => import('@/views/asset/AssetComReg.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 자산관리 협력업체관리
        {
          path: '/asset/AssetPartnerMng',
          name: 'AssetPartnerMng',
          component: () => import('@/views/asset/AssetPartnerMng.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 협력업체관리 계약조회
        {
          path: '/asset/AssetVendorManagement',
          name: 'AssetVendorManagement',
          component: () => import('@/views/asset/AssetVendorManagement.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V'] },
        },
        //Asset Management 자산관리 정기점검현황
        {
          path: '/asset/AssetMaintenanceReportStatus',
          name: 'AssetMaintenanceReportStatus',
          component: () =>
            import('@/views/asset/AssetMaintenanceReportStatus.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V'] },
        },
        //Asset Management 자산관리 유지보수/기술지원 현황
        {
          path: '/asset/AssetMaintenanceStatus',
          name: 'AssetMaintenanceStatus',
          component: () => import('@/views/asset/AssetMaintenanceStatus.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 자산관리 정기점검보고서
        {
          path: '/asset/AssetMaintenanceReportDialog',
          name: 'AssetMaintenanceReportDialog',
          component: () =>
            import('@/views/asset/AssetMaintenanceReportDialog.vue'),
          meta: {
            permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V', 'VENDOR'],
          },
        },
        //Asset Management 자산관리 정기점검보고서
        {
          path: '/asset/AssetMonthlyReport',
          name: 'AssetMonthlyReport',
          component: () => import('@/views/asset/AssetMonthlyReport.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset Management 자산관리 정기점검보고서
        {
          path: '/asset/AssetInspectionReport',
          name: 'AssetInspectionReport',
          component: () => import('@/views/asset/AssetInspectionReport.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        {
          path: '/asset/AssetTechSupportReport',
          name: 'AssetTechSupportReport',
          component: () => import('@/views/asset/AssetTechSupportReport.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'VENDOR'] },
        },
        //Asset Management 자산관리 기술지원요청
        {
          path: '/asset/AssetTechSupportReq',
          name: 'AssetTechSupportReq',
          component: () => import('@/views/asset/AssetTechSupportReq.vue'),
          meta: {
            permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V', 'VENDOR'],
          },
        },
        {
          path: '/asset/AssetMonthlyMaintenanceSummary',
          name: 'AssetMonthlyMaintenanceSummary',
          component: () =>
            import('@/views/asset/AssetMonthlyMaintenanceSummary.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        {
          path: '/asset/AssetCategory',
          name: 'AssetCategory',
          component: () => import('@/views/asset/AssetCategory.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset ManagementIT 자산관리 자산그룹관리
        {
          path: '/asset/AssetGroupMng',
          name: 'AssetGroupMng',
          component: () => import('@/views/asset/AssetGroupManagement.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
        },
        //Asset ManagementIT 자산관리 자산조회
        {
          path: '/asset/AssetManagement',
          name: 'AssetManagement',
          component: () => import('@/views/asset/AssetManagement.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V', 'ASSET_PC'] },
        },
        //Asset ManagementIT 자산관리 제조사관리
        {
          path: '/asset/ManufacMng',
          name: 'ManufacMng',
          component: () =>
            import('@/views/asset/AssetManufacturerManagement.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_PC'] },
        },
        //Asset ManagementIT 자산관리 자산분류관리
        {
          path: '/asset/CatMng',
          name: 'CatMng',
          component: () => import('@/views/asset/CategoryManagement.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_PC'] },
        },
        //Asset Management 협력업체관리 계약조회
        {
          path: '/asset/AssetContMng',
          name: 'AssetContMng',
          component: () => import('@/views/asset/AssetContractMng.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V'] },
        },
        {
          path: '/asset/Users',
          name: 'Users',
          component: () => import('@/views/asset/AssetUserMng.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET_ADMIN', 'ASSET_PC'] },
        },
        //담당자 관리
        {
          path: '/asset/VendorUser',
          name: 'VendorUser',
          component: () => import('@/views/asset/AssetVendorUser.vue'),
          meta: {
            permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_V', 'mainuser'],
          },
        },
        {
          path: '/asset/OfficeMng',
          name: 'AssetOfficeMng',
          component: () => import('@/views/asset/AssetOfficeMng.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'ASSET_PC'] },
        },
        {
          path: '/pages/adChat',
          name: 'adChat',
          component: () => import('@/views/pages/adChat.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET'] },
          props: true,
        },
        {
          path: '/pages/links',
          name: 'links',
          component: () => import('@/views/pages/links.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'ASSET', 'VENDOR'] },
        },
        {
          path: '/sql/sqlGen',
          name: 'sqlGen',
          component: () => import('@/views/sql/sqlGen.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A'] },
          props: true,
        },
        {
          path: '/sql/sqlBuild',
          name: 'sqlBuild',
          component: () => import('@/views/sql/sqlBuilder.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
          props: true,
        },
        {
          path: '/sql/sqlBuildV1',
          name: 'sqlBuildV1',
          component: () => import('@/views/sql/sqlBuilderV3.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
          props: true,
        },
        {
          path: '/sql/colSearch',
          name: 'colSearch',
          component: () => import('@/views/sql/colSearch.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
          props: true,
        },
        {
          path: '/sql/sqlList',
          name: 'sqlList',
          component: () => import('@/views/sql/sqlList.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
          props: true,
        },
        {
          path: '/sql/sqlmng',
          name: 'sqlMng',
          component: () => import('@/views/sql/sqlMng.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'HWPOC', 'lnr_p'] },
          props: true,
        },
        {
          path: '/sql/sqlmngV2',
          name: 'sqlMngV2',
          component: () => import('@/views/sql/sqlMngV2.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'HWPOC'] },
          props: true,
        },
        {
          path: '/lnr/reg',
          name: 'lnrReg',
          component: () => import('@/views/lnr/lnrpost.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'LNR'] },
        },
        {
          path: '/lnr/list',
          name: 'lnrList',
          component: () => import('@/views/lnr/lnrlist.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'LNR'] },
        },
        {
          path: '/pages/comptest',
          name: 'ComponentTest',
          component: () => import('@/views/pages/CompTest.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/chat',
          name: 'chat',
          component: () => import('@/views/pages/Chat.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/chatsql',
          name: 'chatsql',
          component: () => import('@/views/pages/ChatSql.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/cal',
          name: 'cal',
          component: () => import('@/views/pages/GoogleCal.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/tabletest',
          name: 'tabletest',
          component: () => import('@/views/pages/TableTest.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/quill',
          name: 'quilltest',
          component: () => import('@/views/pages/QuillTest.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/e2btest',
          name: 'e2btest',
          component: () => import('@/views/pages/E2BTest.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
        },
        {
          path: '/pages/prjdetail/:prjId',
          name: 'prjDetail',
          component: () => import('@/views/pages/PrjDetail.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
          props: true,
        },
        {
          path: '/pages/sqlview',
          name: 'sqlView',
          component: () => import('@/views/pages/sqlView.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'lnr_p'] },
          props: true,
        },
        {
          path: '/pages/sqlviewprj',
          name: 'sqlViewPrj',
          component: () => import('@/views/pages/sqlViewPrj.vue'),
          props: true,
          meta: { permission: ['ADMIN', 'DM', 'sqlvp_'] },
        },
        {
          path: '/pages/sqlviewsvy',
          name: 'sqlViewSvy',
          component: () => import('@/views/pages/sqlViewSvy.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A', 'SURVEY'] },
          props: true,
        },
        {
          path: '/board/list',
          name: 'blist',
          component: () => import('@/views/board/blist.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A'] },
        },
        {
          path: '/board/post',
          name: 'bpost',
          component: () => import('@/views/board/bpost.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A'] },
        },
        {
          path: '/board/post2',
          name: 'bpost2',
          component: () => import('@/views/board/bpostquillori.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A'] },
        },
        {
          path: '/board/view/:id',
          name: 'bview',
          component: () => import('@/views/board/bview.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A'] },
        },
        {
          path: '/board/edit/:id',
          name: 'bedit',
          component: () => import('@/views/board/bedit.vue'),
          meta: { permission: ['ADMIN', 'ADMIN_A'] },
        },
        {
          path: '/survey/jscadmin',
          name: 'jscQuestionAdmin',
          component: () => import('@/views/survey/jscQuestionAdmin.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/survey/list',
          name: 'surveyList',
          component: () => import('@/views/survey/SurveyList.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/survey/setting',
          name: 'surveySetting',
          component: () => import('@/views/survey/SurveySetting.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/survey/edit/:id',
          name: 'surveyEdit',
          component: () => import('@/views/survey/SurveySetting.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/survey/view',
          name: 'surveyView',
          component: () => import('@/views/survey/SurveyView.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/survey/design',
          name: 'surveyDesign',
          component: () => import('@/views/survey/SurveyDesign.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/survey/design/:id',
          name: 'surveyDesignWithId',
          component: () => import('@/views/survey/SurveyDesign.vue'),
          meta: { permission: ['ADMIN'] },
          props: true,
        },
        {
          path: '/survey/preview/:id',
          name: 'surveyPreview',
          component: () => import('@/views/survey/SurveyPreview.vue'),
          meta: { permission: ['ADMIN'] },
          props: true,
        },
        {
          path: '/survey/participate/:id',
          name: 'surveyParticipate',
          component: () => import('@/views/survey/SurveyParticipate.vue'),
          meta: { 
            title: '설문 참여',
            requiresAuth: true
          },
          props: true,
        },
        {
          path: '/survey/result/:id',
          name: 'surveyResult',
          component: () => import('@/views/survey/SurveyResult.vue'),
          meta: { permission: ['ADMIN'] },
          props: true,
        },
        {
          path: '/survey/participants',
          name: 'surveyParticipants',
          component: () => import('@/views/survey/SurveyParticipantManagement.vue'),
          meta: { permission: ['ADMIN'] },
        },

        {
          path: '/jsc/list',
          name: 'jscList',
          component: () => import('@/views/survey/jscSurveyList.vue'),
          meta: { permission: ['ADMIN'] },
        },
        {
          path: '/uikit/menu',
          component: () => import('@/views/uikit/Menu.vue'),
          children: [
            {
              path: '/uikit/menu',
              component: () => import('@/views/uikit/menu/PersonalDemo.vue'),
            },
            {
              path: '/uikit/menu/seat',
              component: () => import('@/views/uikit/menu/SeatDemo.vue'),
            },
            {
              path: '/uikit/menu/payment',
              component: () => import('@/views/uikit/menu/PaymentDemo.vue'),
            },
            {
              path: '/uikit/menu/confirmation',
              component: () =>
                import('@/views/uikit/menu/ConfirmationDemo.vue'),
            },
          ],
        },
        {
          path: '/blocks',
          name: 'blocks',
          component: () => import('@/views/utilities/Blocks.vue'),
        },
        {
          path: '/utilities/icons',
          name: 'icons',
          component: () => import('@/views/utilities/Icons.vue'),
        },
        {
          path: '/pages/timeline',
          name: 'timeline',
          component: () => import('@/views/pages/Timeline.vue'),
        },
        {
          path: '/pages/empty',
          name: 'empty',
          component: () => import('@/views/pages/Empty.vue'),
        },
        {
          path: '/pages/crud',
          name: 'crud',
          component: () => import('@/views/pages/Crud.vue'),
        },
        {
          path: '/documentation',
          name: 'documentation',
          component: () => import('@/views/utilities/Documentation.vue'),
        },
        {
          path: '/issue/IssueReportList',
          name: 'IssueReportList',
          component: IssueReportList,
          meta: {
            requiresAuth: true,
            title: '고장신고 처리현황',
            excludeVendor: true // 벤더 사용자 제외
          }
        },
        {
          path: '/issue/IssueReportDialog',
          name: 'IssueReportDialog',
          component: IssueReportDialog,
          meta: {
            requiresAuth: true,
            excludeVendor: true // 벤더 사용자 제외
          },
        },
      ],
    },
    {
      path: '/external/survey/participate',
      name: 'externalSurveyParticipate',
      component: () => import('@/views/survey/SurveyParticipate.vue'),
      meta: { 
        title: '외부 설문 참여',
        requiresAuth: false,
        isExternal: true
      },
    },
    {
      path: '/landing',
      name: 'landing',
      component: () => import('@/views/pages/Landing.vue'),
    },
    {
      path: '/pages/notfound',
      name: 'notfound',
      component: () => import('@/views/pages/NotFound.vue'),
    },
    {
      path: '/pages/c',
      name: 'coverage',
      component: () => import('@/views/pages/coverage.vue'),
    },
    {
      path: '/auth/login',
      name: 'login',
      component: () => import('@/views/pages/auth/Login.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/auth/register',
      name: 'register',
      component: () => import('@/views/pages/auth/Register.vue'),
      meta: { 
        requiresAuth: true,
        permission: ['ADMIN']
      }
    },
    {
      path: '/auth/access',
      name: 'accessDenied',
      component: () => import('@/views/pages/auth/Access.vue'),
    },
    {
      path: '/auth/error',
      name: 'error',
      component: () => import('@/views/pages/auth/Error.vue'),
    },
    {
      path: '/asset/a',
      name: 'assetA',
      component: () => import('@/views/asset/AssetV.vue'),
      meta: {
        title: '자산정보',
      },
    },
    {
      path: '/survey/jsc',
      name: 'jsc',
      component: () => import('@/views/survey/SurveyPartJsc.vue'),
      meta: {
        title: 'JSC 참석 설문',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/jscqv',
      name: 'jscQuestionView',
      component: () => import('@/views/survey/jscQuestionViewer.vue'),
      meta: { permission: ['ADMIN'] },
    },
    {
      path: '/survey/jscc',
      name: 'jscCancel',
      component: () => import('@/views/survey/SurveyCancelJsc.vue'),
      meta: {
        title: '등록 취소 관련 요청',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/jscn',
      name: 'jscNot',
      component: () => import('@/views/survey/SurveyNotPartJsc.vue'),
      meta: {
        title: '미참석 관련 설문',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/jscd1',
      name: 'jscD1',
      component: () => import('@/views/survey/SurveyJscD1.vue'),
      meta: {
        title: 'Day1 설문',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/jscd2',
      name: 'jscD2',
      component: () => import('@/views/survey/SurveyJscD2.vue'),
      meta: {
        title: 'Day2 설문',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/jsc/atd/:surveyId',
      name: 'jscAtd',
      component: () => import('@/views/survey/jscAtd.vue'),
      props: (route) => ({
        surveyId: route.params.surveyId,
        ustr: route.query.ustr,
      }),
      meta: {
        title: 'JSC 참석자 관리',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/quiz',
      name: 'quiz',
      component: () => import('@/views/survey/SurveyQuiz.vue'),
      meta: {
        title: '콘퍼런스 관련 퀴즈',
      },
    },
    {
      path: '/survey/q',
      name: 'jscQuestion',
      component: () => import('@/views/survey/jscQuestion.vue'),
      meta: {
        title: '콘퍼런스 관련 질문',
      },
    },
    {
      path: '/survey/u',
      name: 'jscUser',
      component: () => import('@/views/survey/jscUser.vue'),
      meta: {
        title: '2024 콘퍼런스',
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/oxgameu',
      name: 'oxgameu',
      component: () => import('@/views/survey/OxQuizU.vue'),
      meta: {
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/oxgame',
      name: 'oxgame',
      component: () => import('@/views/survey/OxQuiz.vue'),
      meta: {
        permission: ['ADMIN'],
      },
    },
    {
      path: '/survey/oxadmin',
      name: 'oxadmin',
      component: () => import('@/views/survey/OxQuizAdmin.vue'),
      meta: { permission: ['ADMIN'] },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/pages/NotFound.vue'),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const tabStore = useTabStore();
  const layoutStore = useLayoutStore();

  // 인증이 필요한 라우트인지 먼저 확인
  if (to.meta.requiresAuth === false) {
    // 인증이 명시적으로 필요 없는 라우트는 바로 통과
    next();
    return;
  }
  
  if (to.meta.requiresAuth) {
    // 인증 상태가 아직 확인되지 않았다면 체크
    if (!authStore.isAuthChecked) {
      try {
        await authStore.checkAuth();
      } catch (error) {
        console.error('Auth check failed:', error);
        next('/auth/login');
        return;
      }
    }

    // 인증되지 않은 경우 로그인 페이지로 리다이렉트
    if (!authStore.isAuthenticated) {
      next('/auth/login');
      return;
    }

    // 권한 체크
    if (to.meta.permission) {
      const requiredPermissions = Array.isArray(to.meta.permission)
        ? to.meta.permission
        : [to.meta.permission];

      if (!authStore.hasAnyPermission(requiredPermissions)) {
        console.log('Permission denied');
        next('/auth/access');
        return;
      }
    }

    // 벤더 사용자 제한 체크
    if (to.meta.excludeVendor && authStore.isVendorUser) {
      next('/vendor/dashboard');
      return;
    }

    // 벤더 권한 체크 - 벤더 전용 라우트에 대한 접근 제한
    if (to.meta.isVendorRoute && !authStore.isVendorUser) {
      next('/auth/access');
      return;
    }

    // 벤더 사용자 리다이렉트 - 허용된 경로 외에는 벤더 대시보드로 리다이렉트
    if (authStore.isVendorUser) {
      // 벤더 사용자가 접근 가능한 경로 목록
      const allowedVendorPaths = [
        '/vendor/dashboard',
        '/asset/VendorUser',
        '/auth/access',
        '/asset/AssetMaintenanceReportDialog',
        '/asset/AssetTechSupportReq',
        '/asset/AssetMaintenanceReportStatus'
      ];

      if (allowedVendorPaths.includes(to.path)) {
        next();
      } else {
        next('/vendor/dashboard');
      }
      return;
    }

    // 일반 사용자의 모바일 접근 처리
    if (!authStore.isVendorUser && checkIsMobile() && to.path === '/') {
      next('/MobileHome');
      return;
    }

    // 인증이 필요 없는 라우트이거나, 위의 모든 조건을 통과한 경우
    next();
  }

  // 탭 관리 로직 추가
  // login, access 등 특정 페이지는 탭으로 관리하지 않음
  const skipTabRoutes = ['/auth/login', '/auth/access', '/auth/error'];
  if (
    layoutStore.useTabView &&
    !skipTabRoutes.includes(to.path) &&
    !to.meta.skipTab
  ) {
    console.log('Adding tab for route:', to.path); // 디버깅용
    tabStore.addTab({
      path: to.path,
      name: to.name,
      meta: to.meta,
      title: to.meta.title || to.name,
      params: to.params,
      query: to.query,
    });
  }

  // 현재 레이아웃 모드 유지
  // const currentLayout = layoutStore.layoutMode;
  // if (currentLayout) {
  //   layoutStore.setLayoutMode(currentLayout);
  // }
  // 현재 레이아웃 모드 유지
  if (to.state?.layoutMode) {
    layoutStore.setLayoutMode(to.state.layoutMode);
  } else {
    // state가 없는 경우에는 현재 레이아웃 모드를 유지
    const currentLayout = layoutStore.layoutMode;
    if (currentLayout) {
      layoutStore.setLayoutMode(currentLayout);
    }
  }
  // 인증이 필요 없는 라우트이거나, 위의 모든 조건을 통과한 경우
  next();

  // 페이지 제목 설정
  document.title =
    to.meta.title || import.meta.env.VITE_APP_SITE_NAME || 'AssetCare';
});

export default router;
